import { useEffect, useMemo } from 'react';
import { useQueryCmModuleVendors } from 'hooks/cmModule/useQueryCmModuleVendors';

import { Icons } from '@components/common';
import DashboardKpis from '@components/DashboardKpis';
import AlarmManagement from '@components/routes/AdminPanel/AlarmManagement';
import Alarms from '@components/routes/AdminPanel/Alarms';
import AssetCartRequestFullView from '@components/routes/AdminPanel/AssetCartRequestFullView';
import AssetHistory from '@components/routes/AdminPanel/AssetHistory';
import AssetHistoryProfile from '@components/routes/AdminPanel/AssetHistory/AssetHistoryProfile';
import AssetProfile from '@components/routes/AdminPanel/AssetProfile';
import ManageAssetProfile from '@components/routes/AdminPanel/AssetProfile/ManageAssetProfile';
import AssetsDashboard from '@components/routes/AdminPanel/AssetsDashboard';
import AssetsList from '@components/routes/AdminPanel/AssetsList';
import AssetRentIncomingRequests from '@components/routes/AdminPanel/AssetsRent/IncomingRequests';
import AssetRentOutgoingRequests
	from '@components/routes/AdminPanel/AssetsRent/OutgoingRequests/AssetRentOutgoingRequests';
import RequestReservationCardContainerAO
	from '@components/routes/AdminPanel/AssetsRent/RequestReservationCard/RequestReservationCardContainerAO';
import RequestReservationCardContainerMNO
	from '@components/routes/AdminPanel/AssetsRent/RequestReservationCard/RequestReservationCardContainerMNO';
import AssignApprovalUnits from '@components/routes/AdminPanel/AssignApprovalUnits';
import DownloadHistory from '@components/routes/AdminPanel/DownloadHistory';
import KpiAiAnalytics from '@components/routes/AdminPanel/KpiAiAnalytics';
import KpiAlertsManagement from '@components/routes/AdminPanel/KpiAlertsManagement';
import KpiThresholdManagement from '@components/routes/AdminPanel/KpiThresholdManagement';
import ManageAcquiredAssets from '@components/routes/AdminPanel/ManageAcquiredAssets';
import ManageAllCells from '@components/routes/AdminPanel/ManageAllCells';
import ManageApprovalUnits from '@components/routes/AdminPanel/ManageApprovalUnits';
import ManageAssetTypes from '@components/routes/AdminPanel/ManageAssetTypes/ManageAssetTypes';
import ManageCrowdCells from '@components/routes/AdminPanel/ManageCrowdCells';
import ManageErrorCodes from '@components/routes/AdminPanel/ManageErrorCodes/ManageErrorCodes';
import ManageMiddlewareSMO from '@components/routes/AdminPanel/ManageMiddlewareSMO';
import ManageNetworkElementConfigurations from '@components/routes/AdminPanel/ManageNetworkElementConfigurations';
import ChangeMonitoring from '@components/routes/AdminPanel/ManageNetworkElementConfigurations/ChangeMonitoring';
import ManageNetworkElements from '@components/routes/AdminPanel/ManageNetworkElements';
import ManagePlatformBranding from '@components/routes/AdminPanel/ManagePlatformBranding';
import ManageSites from '@components/routes/AdminPanel/ManageSites';
import OrganisationProfileHOC from '@components/routes/AdminPanel/OrganisationProfileHOC';
import Organisations from '@components/routes/AdminPanel/Organisations';
import ProjectProfileHOC from '@components/routes/AdminPanel/ProjectProfileHOC';
import Projects from '@components/routes/AdminPanel/Projects';
import ApiDocumentation from '@components/routes/AdminPanel/Reports/ApiDocumentation';
import RUStatusDashboard from '@components/routes/AdminPanel/RUStatusDashboard';
import SectorsSettings from '@components/routes/AdminPanel/SectorsSettings';
import SiteProfile from '@components/routes/AdminPanel/SiteProfile';
import UserHistory from '@components/routes/AdminPanel/UserHistory';
import UsersMainTableHOC from '@components/routes/AdminPanel/UsersMainTableHOC';
import VesEventsTable from '@components/routes/AdminPanel/VesEvents';
import ConfigurationComparison from '@components/routes/CmModule/ConfigurationComparison';
import ConfigurationDataModelManagement from '@components/routes/CmModule/ConfigurationDataModelManagement';
import ConfigurationTemplates from '@components/routes/CmModule/ConfigurationTemplates';
import ConsistencyCheckHistory from '@components/routes/CmModule/ConsistencyCheck/ConsistencyCheckHistory';
import ConsistencyCheckWrapper from '@components/routes/CmModule/ConsistencyCheck/ConsistencyCheckWrapper';
import DataSources from '@components/routes/CmModule/DataSource';
import HistoryCheck from '@components/routes/CmModule/HistoryCheck';
import OperationRules from '@components/routes/CmModule/OperationWindowRules/OperationWindowRules';
import RUConfigurationManagement from '@components/routes/CmModule/RUConfigurationManagement';
import Settings from '@components/routes/Settings/Settings';

import { useQueryAlarmVendors } from '@hooks/alarms/useQueryAlarmVendors';
import { useQueryAssetCartRequestCount } from '@hooks/assets/useQueryAssetCartRequestCount';
import { useQueryConfigurationComparisonVendors } from '@hooks/cmModule/useQueryConfigurationComparisonVendors';
import useQueryConsistencyCheckVendors from '@hooks/consistencyCheck/useQueryConsistencyCheckVendors';
import { useQueryHistoryCheckVendors } from '@hooks/historyCheck/useQueryHistoryCheckVendors';
import { useQueryKpiVendors } from '@hooks/kpis/useQueryKpiVendors';
import { useQuerySiteVendors } from '@hooks/useQuerySiteVendors';
import { useQueryUserDetails } from '@hooks/useQueryUserDetails';

import { auth, common, configurationManagement } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setVendorDrivenFeatures } from '@slices/configurationManagement';

import { checkFeatureAvailabilityForVendor } from '@utils/checkFeatureAvailabilityForVendor';
import { checkGroupPanelAccess } from '@utils/permissions';
import { toSideBarMenuItems } from '@utils/sideBar';

import { IRouteItem } from '@components/common/RoutesWrapper/interfaces';
import { IMenuItem, TSideBarGroups } from '@components/common/SideBar/interfaces';

import { EAdminPanelPageTitles, EGroupMenuTitles, EVendorsValues, TVendorsValues } from '@enums/common';

import { DEFAULT_ADMIN_PANEL_ROUTES } from '@constants/common';
import {
	cmModuleGroupAccess,
	configurationDataModelManagementGroupAccess,
	configurationTemplatesGroupAccess,
	manageNetworkElementConfigurationAccess,
	operationRulesGroupAccess,
} from '@constants/menuGroupAccess';
import { routes } from '@constants/routes';

const ALARM_VENDORS: TVendorsValues[] = [
	EVendorsValues.limenet,
	EVendorsValues.flexidas,
	EVendorsValues.oran,
	EVendorsValues.benetel,
];

const CORE_HDD_ORGANIZATION_ID = '66cf24d4a39dcc235ed01a92';

const {
	adminPanel: {
		acquiredAssetProfile,
		alarmManagement,
		alarms,
		assetHistory,
		assetHistoryProfile,
		assetProfile,
		assetTypes,
		assets,
		assetsDashboard,
		assignApprovalUnits,
		downloadHistory,
		errors,
		incomingRequestCardAssetProfile,
		incomingRequestPath,
		incomingRequestsPath,
		index,
		kpiAiAnalytics,
		kpiDashboard,
		kpiNotifications,
		manageAcquiredAssets,
		manageAllSites,
		manageApproveUnits,
		manageCrowdCells,
		managePlatformBranding,
		manageSite,
		middlewareSMO,
		networkElements,
		organisationProfile,
		organisationSettings,
		organisations,
		outgoingRequestCardAssetProfile,
		outgoingRequestPath,
		outgoingRequestsPath,
		portalProjectProfile,
		portalProjects,
		requestListPath,
		ruStatusDashboard,
		sectorsSettings,
		siteProfile,
		thresholdManagementAccessPath,
		userHistory,
		users,
		vesEvents,
	},
	cmModule,
	reports,
} = routes;

const adminSideBarGroups: Partial<TSideBarGroups> = {
	[EGroupMenuTitles.Portal]: {
		title: EGroupMenuTitles.Portal,
		icon: Icons.portalIcon,
		activeIcon: () => Icons.portalIcon({ isActive: true }),
		order: 3,
	},
	[EGroupMenuTitles.AccessManagement]: {
		title: EGroupMenuTitles.AccessManagement,
		icon: Icons.bag,
		activeIcon: () => Icons.bag({ isActive: true }),
		order: 1,
	},
	[EGroupMenuTitles.Settings]: {
		title: EGroupMenuTitles.Settings,
		icon: Icons.settings,
		activeIcon: () => Icons.settingsPainted({ innerSvgProps: true } as any),
		order: 4,
	},
	[EGroupMenuTitles.TelecomSites]: {
		title: EGroupMenuTitles.TelecomSites,
		icon: Icons.manageSiteDB,
		activeIcon: Icons.manageSiteDBInverse,
		order: 8,
	},
	[EGroupMenuTitles.KPI]: {
		title: EGroupMenuTitles.KPI,
		icon: Icons.kpiIconOutline,
		activeIcon: Icons.kpiIcon,
		order: 9,
	},
	[EGroupMenuTitles.AcquireAssets]: {
		title: EGroupMenuTitles.AcquireAssets,
		icon: Icons.assetsRequests,
		activeIcon: Icons.assetsRequestDark,
		order: 5,
	},
	[EGroupMenuTitles.Assets]: {
		title: EGroupMenuTitles.Assets,
		icon: Icons.assets,
		activeIcon: Icons.assetsActive,
		order: 6,
	},
	[EGroupMenuTitles.Alarm]: {
		title: EGroupMenuTitles.Alarm,
		icon: Icons.alarm,
		activeIcon: Icons.alarmActive,
		order: 10,
	},
	[EGroupMenuTitles.AssetTypesManagement]: {
		title: EGroupMenuTitles.AssetTypesManagement,
		icon: Icons.layer,
		activeIcon: Icons.layer,
		order: 7,
	},
	[EGroupMenuTitles.AssetsDashboard]: {
		title: EGroupMenuTitles.AssetsDashboard,
		icon: Icons.dashboardIcon,
		activeIcon: () => Icons.dashboardIcon({ isActive: true }),
		order: 2,
	},
	[EGroupMenuTitles.CMModule]: {
		title: EGroupMenuTitles.CMModule,
		icon: Icons.cmModule,
		activeIcon: Icons.cmModule,
		isVendorSpecific: true,
		order: 12,
	},
	[EGroupMenuTitles.Reports]: {
		title: EGroupMenuTitles.Reports,
		icon: Icons.reports,
		activeIcon: () => Icons.reports({ isActive: true }),
		order: 11,
	},
	[EGroupMenuTitles.SMOLight]: {
		title: EGroupMenuTitles.SMOLight,
		icon: Icons.middlewareSMO,
		activeIcon: () => Icons.middlewareSMO({ isActive: true }),
		order: 13,
	},
};

export const useAdminPanelRoutes = () => {
	const authState = useAppSelector(auth);
	const { isProductionIdams } = useAppSelector(common);
	const dispatch = useAppDispatch();

	const {
		accessPermission:
			{
				assetConsumerAssetEnquirer,
				assetConsumerOrganizationAdministrator,
				assetOwnerApprovalUnit,
				assetOwnerAttributeUpdater,
				assetOwnerContact,
				assetOwnerDefaultContact,
				assetOwnerManageRequests,
				assetOwnerOrganizationAdministrator,
				assetOwnerSignatory,
				assetOwnerViewer,
				networkOperatorAlarmManager,
				networkOperatorAssetEnquirer,
				networkOperatorCmModuleEditor,
				networkOperatorKpiUpdater,
				networkOperatorKpiViewer,
				networkOperatorOrganizationAdministrator,
				networkOperatorTelecomSitesAttributeUpdater,
				networkOperatorTelecomSitesViewer,
				networkOperatorThresholdKpiUpdater,
				networkOperatorThresholdKpiViewer,
				platformOwnerAdministrator,
				portalProjectAdministrator,
				portalProjectMember,
				smoLightUpdater,
				smoLightViewer,
				networkOperatorAssetViewer,
				networkOperatorCmModuleViewer,
			},
	} = authState;

	const { user, isLoadingCurrentUser } = useQueryUserDetails();

	const organizationId = user?.organization?.id;

	const availableVendors = user?.organization?.availableVendors;

	const {
		vendorsOptions: configurationComparisonVendors,
		allVendorsOptions,
		isLoadingVendorsOptions,
	} = useQueryConfigurationComparisonVendors();
	const { optionsVendorsKpi } = useQueryKpiVendors(true);
	const { assetCartRequestListCount } = useQueryAssetCartRequestCount();
	const { vendors: siteVendors } = useQuerySiteVendors();
	const { alarmVendors, isLoadingAlarmVendors } = useQueryAlarmVendors();
	const { vendorsOptions: consistencyCheckVendors } = useQueryConsistencyCheckVendors();
	const { vendorsOptions: historyCheckVendors } = useQueryHistoryCheckVendors();
	const { cmModuleVendorOptions } = useQueryCmModuleVendors();

	const isSitesAvailableForVendors = checkFeatureAvailabilityForVendor(
		user?.organization?.availableVendors,
		[EVendorsValues.oran, EVendorsValues.flexidas],
	);

	const isLimenetVendor = !!siteVendors.find(({ name }) => name === EVendorsValues.limenet);
	const isMavenirVendor = useMemo(() => user?.organization?.availableVendors?.some(({ name }) =>
		name === EVendorsValues.mavenir), [user?.organization?.availableVendors]);
	const isOranVendor = useMemo(() => !!user?.organization?.availableVendors?.some(({ name }) =>
		name === EVendorsValues.oran), [user?.organization?.availableVendors]);
	const isBenetelVendor = useMemo(() => !!user?.organization?.availableVendors?.some(({ name }) =>
		name === EVendorsValues.benetel), [user?.organization?.availableVendors]);

	const isKpiAvailable = optionsVendorsKpi?.length && availableVendors?.length;
	const isSitesAvailable = siteVendors.length && availableVendors?.length && isSitesAvailableForVendors;
	const isConfigurationComparisonAvailable = configurationComparisonVendors.length && availableVendors?.length;
	const isConsistencyCheckAvailable = consistencyCheckVendors.length && availableVendors?.length ||
		availableVendors?.findIndex(item => item.name === EVendorsValues.oran) !== -1;
	const isHistoryCheckAvailable = (historyCheckVendors.length || cmModuleVendorOptions.length) &&
		availableVendors?.length;
	const isCoreHDDOrganization = organizationId === CORE_HDD_ORGANIZATION_ID;
	const isKpiAiAnalyticsPageAccess = !(isOranVendor || isBenetelVendor)
		&& isCoreHDDOrganization
		&& (networkOperatorKpiViewer || networkOperatorKpiUpdater);

	const isRUConfigurationAvailable = true;

	const isAlarmShown = !!alarmVendors?.find(({ name }) => {
		return ALARM_VENDORS.includes(name);
	});
	const isManageOrganisationsUsersErrorsAccess = platformOwnerAdministrator;
	const isManageAssetAccess = (
		assetOwnerOrganizationAdministrator ||
		assetOwnerViewer ||
		assetOwnerContact ||
		assetOwnerDefaultContact ||
		assetOwnerAttributeUpdater ||
		assetOwnerSignatory ||
		assetOwnerManageRequests ||
		assetOwnerApprovalUnit
	);

	const isDownloadHistoryAccess = !isProductionIdams && (
		platformOwnerAdministrator ||
		networkOperatorKpiViewer ||
		networkOperatorKpiUpdater ||
		networkOperatorThresholdKpiViewer ||
		networkOperatorThresholdKpiUpdater ||
		networkOperatorAlarmManager ||
		networkOperatorAssetEnquirer ||
		networkOperatorAssetViewer ||
		assetOwnerOrganizationAdministrator ||
		assetOwnerViewer ||
		assetOwnerAttributeUpdater ||
		assetOwnerContact ||
		assetOwnerSignatory ||
		assetOwnerManageRequests ||
		assetOwnerApprovalUnit ||
		assetConsumerAssetEnquirer ||
		networkOperatorTelecomSitesViewer ||
		networkOperatorTelecomSitesAttributeUpdater ||
		networkOperatorCmModuleEditor ||
		networkOperatorCmModuleViewer ||
		portalProjectAdministrator ||
		portalProjectMember ||
		smoLightViewer ||
		smoLightUpdater
	);
	const isManageSitesDBAccess = networkOperatorTelecomSitesViewer || networkOperatorTelecomSitesAttributeUpdater;
	const isSectorsSettingsAccess = networkOperatorTelecomSitesAttributeUpdater || networkOperatorTelecomSitesViewer;

	const isKpiDashboardAccess = networkOperatorKpiUpdater || networkOperatorKpiViewer;

	const isThresholdManagementAccess = (
		networkOperatorKpiUpdater ||
		networkOperatorThresholdKpiViewer ||
		networkOperatorThresholdKpiUpdater
	);

	const isAlarmAccess = networkOperatorAlarmManager && isAlarmShown;

	const isIncomingRequestAccess = (
		assetOwnerSignatory ||
		assetOwnerManageRequests ||
		assetOwnerApprovalUnit
	);

	const isOutgoingRequestAccess = networkOperatorAssetEnquirer || assetConsumerAssetEnquirer;

	const isSettingsAccess = (
		assetOwnerOrganizationAdministrator ||
		networkOperatorOrganizationAdministrator ||
		assetConsumerOrganizationAdministrator
	);

	const isDataSourcesAccess = allVendorsOptions?.some((vendor) => (
		vendor?.type?.name === EVendorsValues.mavenir),
	) && networkOperatorCmModuleEditor;

	const isUserHistoryAccess = (
		platformOwnerAdministrator ||
		assetOwnerOrganizationAdministrator ||
		networkOperatorOrganizationAdministrator ||
		assetConsumerOrganizationAdministrator
	);

	const isManageNetworkElementConfigurationAccess =
		!(checkGroupPanelAccess(
			authState.accessPermission,
			manageNetworkElementConfigurationAccess,
		) && isOranVendor);

	const isSMOLightAccess = (smoLightViewer || smoLightUpdater) && (isOranVendor || isBenetelVendor);
	const isRUStatusDashboardAccess = (smoLightViewer || smoLightUpdater);

	const isPortalAccess = (portalProjectAdministrator || portalProjectMember) && !isProductionIdams;

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const menuItems: IMenuItem[] = useMemo(() => [
		{
			title: EAdminPanelPageTitles.Projects,
			route: `${index}${portalProjects}`,
			icon: Icons.portalIcon,
			isHidden: !isPortalAccess,
			parent: EGroupMenuTitles.Portal,
		},
		{
			title: EAdminPanelPageTitles.Dashboard,
			route: `${index}${assetsDashboard}`,
			icon: Icons.dashboardIcon,
			isHidden: !platformOwnerAdministrator,
			parent: EGroupMenuTitles.AssetsDashboard,
		},
		{
			title: EAdminPanelPageTitles.ManageOrganisations,
			route: `${index}${organisations}`,
			icon: Icons.bag,
			isHidden: !platformOwnerAdministrator,
			parent: EGroupMenuTitles.AccessManagement,
		},
		{
			title: EAdminPanelPageTitles.ManagePlatformBranding,
			route: `${index}${managePlatformBranding}`,
			icon: Icons.bag,
			isHidden: !platformOwnerAdministrator,
			parent: EGroupMenuTitles.AccessManagement,
		},
		{
			title: EAdminPanelPageTitles.ManageUsers,
			route: `${index}${users}`,
			icon: Icons.users,
			isHidden: !platformOwnerAdministrator,
			parent: EGroupMenuTitles.AccessManagement,
		},
		{
			title: EAdminPanelPageTitles.OrganisationSettings,
			route: `${index}${organisationSettings}`,
			icon: Icons.history,
			activeIcon: Icons.history,
			isHidden: !assetOwnerOrganizationAdministrator &&
				!networkOperatorOrganizationAdministrator &&
				!assetConsumerOrganizationAdministrator,
			parent: EGroupMenuTitles.Settings,
		},
		{
			title: EAdminPanelPageTitles.UserHistory,
			route: `${index}${userHistory}`,
			icon: Icons.history,
			activeIcon: Icons.history,
			isHidden: !platformOwnerAdministrator &&
				!assetOwnerOrganizationAdministrator &&
				!networkOperatorOrganizationAdministrator &&
				!assetConsumerOrganizationAdministrator,
			parent: EGroupMenuTitles.Settings,
		},
		{
			title: EAdminPanelPageTitles.ManageErrorCodes,
			route: `${index}${errors}`,
			icon: Icons.errorPlateIcon,
			activeIcon: Icons.errorPlateActiveIcon,
			isHidden: !platformOwnerAdministrator,
			parent: EGroupMenuTitles.Settings,
		},
		{
			title: EAdminPanelPageTitles.DownloadHistory,
			route: `${index}${downloadHistory}`,
			icon: Icons.settings,
			activeIcon: Icons.settings,
			isHidden: !isDownloadHistoryAccess,
			parent: EGroupMenuTitles.Settings,
		},
		{
			title: EAdminPanelPageTitles.Ruckus,
			titleIcon: Icons.externalRedirect,
			route: 'https://eu.ruckus.cloud',
			isHidden: true,
			parent: EGroupMenuTitles.Settings,
			url: 'https://eu.ruckus.cloud',
		},
		{
			title: EAdminPanelPageTitles.ManageAssets,
			route: `${index}${assets}`,
			icon: Icons.assets,
			activeIcon: Icons.assetsActive,
			isHidden: !isManageAssetAccess,
			parent: EGroupMenuTitles.Assets,
		},
		{
			title: EAdminPanelPageTitles.ManageAssetTypes,
			route: `${index}${assetTypes}`,
			icon: Icons.assetTypesManagement,
			activeIcon: Icons.assetTypesManagement,
			isHidden: !platformOwnerAdministrator,
			parent: EGroupMenuTitles.AssetTypesManagement,
		},
		{
			title: EAdminPanelPageTitles.RequestList,
			route: `${index}${requestListPath}`,
			icon: () => Icons.cartRequest({ count: assetCartRequestListCount }),
			activeIcon: () => Icons.cartRequestActive({ count: assetCartRequestListCount }),
			isHidden: !(networkOperatorAssetEnquirer || assetConsumerAssetEnquirer),
			parent: EGroupMenuTitles.AcquireAssets,
		},
		{
			title: EAdminPanelPageTitles.IncomingRequests,
			route: `${index}${incomingRequestsPath}`,
			icon: Icons.incomingRequest,
			activeIcon: Icons.incomingRequestActive,
			isHidden: platformOwnerAdministrator || (!assetOwnerManageRequests &&
				!assetOwnerSignatory &&
				!assetOwnerApprovalUnit),
			parent: EGroupMenuTitles.AcquireAssets,
		},
		{
			title: EAdminPanelPageTitles.OutgoingRequests,
			route: `${index}${outgoingRequestsPath}`,
			icon: Icons.outgoingRequest,
			activeIcon: Icons.outgoingRequestActive,
			isHidden: platformOwnerAdministrator || !(networkOperatorAssetEnquirer || assetConsumerAssetEnquirer),
			parent: EGroupMenuTitles.AcquireAssets,
		},
		{
			title: EAdminPanelPageTitles.ManageAcquiredAssets,
			route: `${index}${manageAcquiredAssets}`,
			icon: Icons.assetsRequests,
			activeIcon: Icons.assetsRequestDark,
			isHidden: !(networkOperatorAssetEnquirer || assetConsumerAssetEnquirer),
			parent: EGroupMenuTitles.AcquireAssets,
		},
		{
			title: EAdminPanelPageTitles.ManageApprovalUnits,
			route: `${index}${manageApproveUnits}`,
			icon: Icons.assetsRequests,
			activeIcon: Icons.assetsRequestDark,
			isHidden: !assetOwnerOrganizationAdministrator,
			parent: EGroupMenuTitles.AcquireAssets,
		},
		{
			title: EAdminPanelPageTitles.ApprovalUnitAssignment,
			route: `${index}${assignApprovalUnits}`,
			icon: Icons.assetsRequests,
			activeIcon: Icons.assetsRequestDark,
			isHidden: !assetOwnerOrganizationAdministrator,
			parent: EGroupMenuTitles.AcquireAssets,
		},
		{
			title: EAdminPanelPageTitles.KPIDashboard,
			route: `${index}${kpiDashboard}`,
			icon: Icons.kpiIconOutline,
			activeIcon: Icons.kpiIcon,
			isHidden: !((networkOperatorKpiUpdater || networkOperatorKpiViewer) && isKpiAvailable),
			parent: EGroupMenuTitles.KPI,
		},
		{
			title: EAdminPanelPageTitles.KPIThresholds,
			route: `${index}${thresholdManagementAccessPath}`,
			icon: Icons.thresholdManagement,
			activeIcon: Icons.thresholdManagementActive,
			isHidden: !(isThresholdManagementAccess && isKpiAvailable),
			parent: EGroupMenuTitles.KPI,
		},
		{
			title: EAdminPanelPageTitles.KPINotifications,
			route: `${index}${kpiNotifications}`,
			icon: Icons.kpiNotifications,
			activeIcon: Icons.kpiNotificationsOutline,
			isHidden: !(networkOperatorKpiUpdater && isKpiAvailable),
			parent: EGroupMenuTitles.KPI,
		},
		{
			title: EAdminPanelPageTitles.KPIAIAnalytics,
			route: `${index}${kpiAiAnalytics}`,
			icon: Icons.kpiIconOutline,
			activeIcon: Icons.kpiIcon,
			isHidden: !isKpiAiAnalyticsPageAccess,
			parent: EGroupMenuTitles.KPI,
		},
		{
			title: EAdminPanelPageTitles.ManageFlex5GUnits,
			route: `${index}${manageCrowdCells}`,
			icon: Icons.crowdCell,
			activeIcon: Icons.crowdCellActive,
			isHidden: !(networkOperatorTelecomSitesAttributeUpdater && isLimenetVendor && isSitesAvailable),
			parent: EGroupMenuTitles.TelecomSites,
		},
		{
			title: EAdminPanelPageTitles.ManageSitesDB,
			route: `${index}${manageSite}`,
			icon: Icons.manageSiteDB,
			activeIcon: Icons.manageSiteDB,
			isHidden: !(
				(networkOperatorTelecomSitesViewer || networkOperatorTelecomSitesAttributeUpdater) &&
				isSitesAvailable
			),
			parent: EGroupMenuTitles.TelecomSites,
		},
		{
			title: EAdminPanelPageTitles.AllSitesCells,
			route: `${index}${manageAllSites}`,
			icon: Icons.manageAllSiteDB,
			activeIcon: Icons.manageAllSiteDB,
			isHidden: !(
				(networkOperatorTelecomSitesViewer || networkOperatorTelecomSitesAttributeUpdater) &&
				isSitesAvailable
			),
			parent: EGroupMenuTitles.TelecomSites,
		},
		{
			title: EAdminPanelPageTitles.SectorsSettings,
			route: `${index}${sectorsSettings}`,
			icon: Icons.manageSiteDB,
			activeIcon: Icons.manageSiteDB,
			isHidden: !(isSectorsSettingsAccess && isSitesAvailable),
			parent: EGroupMenuTitles.TelecomSites,
		},
		{
			title: EAdminPanelPageTitles.AlarmManagement,
			route: `${index}${alarmManagement}`,
			icon: Icons.alarm,
			activeIcon: Icons.alarmActive,
			isHidden: !isAlarmAccess,
			parent: EGroupMenuTitles.Alarm,
		},
		{
			title: EAdminPanelPageTitles.Alarms,
			route: `${index}${alarms}`,
			icon: Icons.alarm,
			activeIcon: Icons.alarmActive,
			isHidden: !isAlarmAccess,
			parent: EGroupMenuTitles.Alarm,
		},
		{
			title: EAdminPanelPageTitles.DataSources,
			route: `${index}${cmModule.dataSources}`,
			isHidden: !checkGroupPanelAccess(authState.accessPermission, cmModuleGroupAccess) || !isDataSourcesAccess,
			parent: EGroupMenuTitles.CMModule,
			vendors: [EVendorsValues.mavenir],
		},
		{
			title: EAdminPanelPageTitles.ConfigurationComparison,
			route: `${index}${cmModule.configurationComparison}`,
			isHidden: !(
				checkGroupPanelAccess(authState.accessPermission, cmModuleGroupAccess) &&
				isConfigurationComparisonAvailable
			),
			parent: EGroupMenuTitles.CMModule,
			vendors: [EVendorsValues.limenet, EVendorsValues.mavenir, EVendorsValues.oran],
		},
		{
			title: EAdminPanelPageTitles.RUConfiguration,
			route: `${index}${cmModule.ruConfiguration}`,
			isHidden: !(
				checkGroupPanelAccess(authState.accessPermission, cmModuleGroupAccess) &&
				isRUConfigurationAvailable
			),
			parent: EGroupMenuTitles.CMModule,
			vendors: [EVendorsValues.benetel],
		},
		{
			title: EAdminPanelPageTitles.ConsistencyCheck,
			route: `${index}${cmModule.consistencyCheck}`,
			isHidden: !(
				checkGroupPanelAccess(authState.accessPermission, cmModuleGroupAccess) &&
				isConsistencyCheckAvailable
			),
			parent: EGroupMenuTitles.CMModule,
			vendors: [EVendorsValues.limenet, EVendorsValues.mavenir, EVendorsValues.oran],
		},
		{
			title: EAdminPanelPageTitles.HistoryCheck,
			route: `${index}${cmModule.historyCheck}`,
			isHidden: !(
				checkGroupPanelAccess(authState.accessPermission, cmModuleGroupAccess) &&
				isHistoryCheckAvailable
			),
			parent: EGroupMenuTitles.CMModule,
			vendors: [EVendorsValues.limenet, EVendorsValues.mavenir, EVendorsValues.oran],
		},
		{
			title: EAdminPanelPageTitles.ManageNetworkElementConfigurations,
			route: `${index}${cmModule.networkElementConfigurations}`,
			isHidden: isManageNetworkElementConfigurationAccess,
			parent: EGroupMenuTitles.CMModule,
			vendors: [EVendorsValues.oran],
		},
		{
			title: EAdminPanelPageTitles.Schema,
			route: `${index}${cmModule.configurationDataModelManagement}`,
			isHidden: !(checkGroupPanelAccess(
					authState.accessPermission, configurationDataModelManagementGroupAccess) &&
				(isMavenirVendor || isOranVendor)
			),
			parent: EGroupMenuTitles.CMModule,
			vendors: [EVendorsValues.mavenir, EVendorsValues.oran],
		},
		{
			title: EAdminPanelPageTitles.ConfigurationTemplates,
			route: `${index}${cmModule.configurationTemplates}`,
			isHidden: !(checkGroupPanelAccess(
					authState.accessPermission, configurationTemplatesGroupAccess) && (isOranVendor)
			),
			parent: EGroupMenuTitles.CMModule,
			vendors: [EVendorsValues.oran],
		},
		{
			title: EAdminPanelPageTitles.OperationRules,
			route: `${index}${cmModule.operationRules}`,
			isHidden: !(checkGroupPanelAccess(
				authState.accessPermission, operationRulesGroupAccess) && (isOranVendor)
			),
			parent: EGroupMenuTitles.CMModule,
			vendors: [EVendorsValues.oran],
		},
		{
			title: EAdminPanelPageTitles.SMOLightMiddleware,
			route: `${index}${middlewareSMO}`,
			isHidden: !isSMOLightAccess,
			parent: EGroupMenuTitles.SMOLight,
		},
		{
			title: EAdminPanelPageTitles.NetworkElements,
			route: `${index}${networkElements}`,
			isHidden: !isSMOLightAccess,
			parent: EGroupMenuTitles.SMOLight,
		},
		{
			title: EAdminPanelPageTitles.RUStatusDashboard,
			route: `${index}${ruStatusDashboard}`,
			isHidden: !isRUStatusDashboardAccess,
			parent: EGroupMenuTitles.SMOLight,
			vendors: [EVendorsValues.benetel],
		},
		{
			title: EAdminPanelPageTitles.VESEvents,
			route: `${index}${vesEvents}`,
			isHidden: !isSMOLightAccess,
			parent: EGroupMenuTitles.SMOLight,
		},
		{
			title: EAdminPanelPageTitles.APIDocumentation,
			route: `${index}${reports.index}`,
			isHidden: !platformOwnerAdministrator,
			parent: EGroupMenuTitles.Reports,
		},
	], [
		assetCartRequestListCount,
		assetConsumerAssetEnquirer,
		assetConsumerOrganizationAdministrator,
		assetOwnerApprovalUnit,
		assetOwnerManageRequests,
		assetOwnerOrganizationAdministrator,
		assetOwnerSignatory,
		authState.accessPermission,
		isAlarmAccess,
		isConfigurationComparisonAvailable,
		isConsistencyCheckAvailable,
		isDataSourcesAccess,
		isHistoryCheckAvailable,
		isKpiAvailable,
		isKpiAiAnalyticsPageAccess,
		isLimenetVendor,
		isManageAssetAccess,
		isManageNetworkElementConfigurationAccess,
		isMavenirVendor,
		isOranVendor,
		isPortalAccess,
		isSMOLightAccess,
		isSectorsSettingsAccess,
		isSitesAvailable,
		isThresholdManagementAccess,
		networkOperatorAssetEnquirer,
		networkOperatorKpiUpdater,
		networkOperatorKpiViewer,
		networkOperatorOrganizationAdministrator,
		networkOperatorTelecomSitesAttributeUpdater,
		networkOperatorTelecomSitesViewer,
		platformOwnerAdministrator,
		isDownloadHistoryAccess,
		isRUStatusDashboardAccess,
		isRUConfigurationAvailable,
	]);

	const isCmModuleAccess = checkGroupPanelAccess(authState.accessPermission, cmModuleGroupAccess);

	const isConfigurationDataModelManagement = checkGroupPanelAccess(
		authState.accessPermission,
		configurationDataModelManagementGroupAccess,
	);

	const routesList: IRouteItem[] = [
		{
			condition: isPortalAccess,
			Component: Projects,
			path: portalProjects,
		},
		{
			condition: isPortalAccess,
			Component: ProjectProfileHOC,
			path: portalProjectProfile,
		},
		{
			condition: isManageOrganisationsUsersErrorsAccess,
			Component: Organisations,
			path: organisations,
		},
		{
			condition: isManageOrganisationsUsersErrorsAccess,
			Component: ManagePlatformBranding,
			path: managePlatformBranding,
		},
		{
			condition: isManageOrganisationsUsersErrorsAccess,
			Component: UsersMainTableHOC,
			path: users,
		},
		{
			condition: platformOwnerAdministrator,
			Component: OrganisationProfileHOC,
			path: organisationProfile,
		},
		{
			condition: isManageAssetAccess,
			Component: AssetsList,
			path: assets,
		},
		{
			condition: isManageAssetAccess,
			Component: ManageAssetProfile,
			path: assetProfile,
		},
		{
			condition: networkOperatorAssetEnquirer || assetConsumerAssetEnquirer,
			Component: AssetProfile,
			path: acquiredAssetProfile,
		},
		{
			condition: isManageAssetAccess,
			Component: AssetProfile,
			path: incomingRequestCardAssetProfile,
		},
		{
			condition: networkOperatorAssetEnquirer || assetConsumerAssetEnquirer,
			Component: AssetProfile,
			path: outgoingRequestCardAssetProfile,
		},
		{
			condition: isManageAssetAccess,
			Component: AssetHistory,
			path: assetHistory,
		},
		{
			condition: isManageAssetAccess,
			Component: AssetHistoryProfile,
			path: assetHistoryProfile,
		},
		{
			condition: assetOwnerOrganizationAdministrator,
			Component: ManageApprovalUnits,
			path: manageApproveUnits,
		},
		{
			condition: assetOwnerOrganizationAdministrator,
			Component: AssignApprovalUnits,
			path: assignApprovalUnits,
		},
		{
			condition: isManageOrganisationsUsersErrorsAccess,
			Component: ManageErrorCodes,
			path: errors,
		},
		{
			condition: isDownloadHistoryAccess,
			Component: DownloadHistory,
			path: downloadHistory,
		},
		{
			condition: isManageSitesDBAccess,
			Component: ManageSites,
			path: manageSite,
		},
		{
			condition: isManageSitesDBAccess,
			Component: SiteProfile,
			path: siteProfile,
		},
		{
			condition: isManageSitesDBAccess,
			Component: ManageAllCells,
			path: manageAllSites,
		},
		{
			condition: isSectorsSettingsAccess,
			Component: SectorsSettings,
			path: sectorsSettings,
		},
		{
			condition: networkOperatorTelecomSitesAttributeUpdater,
			Component: ManageCrowdCells,
			path: manageCrowdCells,
		},
		{
			condition: isUserHistoryAccess,
			Component: UserHistory,
			path: userHistory,
		},
		{
			condition: isSettingsAccess,
			Component: Settings,
			path: organisationSettings,
		},
		{
			condition: isKpiDashboardAccess,
			Component: DashboardKpis,
			path: kpiDashboard,
		},
		{
			condition: isKpiAiAnalyticsPageAccess,
			Component: KpiAiAnalytics,
			path: kpiAiAnalytics,
		},
		{
			condition: isThresholdManagementAccess,
			Component: KpiThresholdManagement,
			path: thresholdManagementAccessPath,
		},
		{
			condition: true,
			Component: KpiAlertsManagement,
			path: kpiNotifications,
		},
		{
			condition: networkOperatorAssetEnquirer || assetConsumerAssetEnquirer,
			Component: AssetCartRequestFullView,
			path: requestListPath,
		},
		{
			condition: networkOperatorAssetEnquirer || assetConsumerAssetEnquirer,
			Component: ManageAcquiredAssets,
			path: manageAcquiredAssets,
		},
		{
			condition: isIncomingRequestAccess,
			Component: RequestReservationCardContainerAO,
			path: incomingRequestPath,
		},
		{
			condition: isIncomingRequestAccess,
			Component: AssetRentIncomingRequests,
			path: incomingRequestsPath,
		},
		{
			condition: isOutgoingRequestAccess,
			Component: RequestReservationCardContainerMNO,
			path: outgoingRequestPath,
		},
		{
			condition: isOutgoingRequestAccess,
			Component: AssetRentOutgoingRequests,
			path: outgoingRequestsPath,
		},
		{
			condition: isAlarmAccess,
			Component: AlarmManagement,
			path: alarmManagement,
		},
		{
			condition: isAlarmAccess,
			Component: Alarms,
			path: alarms,
		},
		{
			condition: platformOwnerAdministrator,
			Component: ManageAssetTypes,
			path: assetTypes,
		},
		{
			condition: platformOwnerAdministrator,
			Component: AssetsDashboard,
			path: assetsDashboard,
		},
		{
			condition: isCmModuleAccess,
			Component: RUConfigurationManagement,
			path: cmModule.ruConfiguration,
		},
		{
			condition: isCmModuleAccess,
			Component: ConfigurationComparison,
			path: cmModule.configurationComparison,
		},
		{
			condition: isCmModuleAccess,
			Component: ConsistencyCheckWrapper,
			path: cmModule.consistencyCheck,
		},
		{
			condition: isCmModuleAccess,
			Component: ConsistencyCheckHistory,
			path: cmModule.consistencyCheckHistory,
		},
		{
			condition: isCmModuleAccess,
			Component: HistoryCheck,
			path: cmModule.historyCheck,
		},
		{
			condition: !isManageNetworkElementConfigurationAccess,
			Component: ManageNetworkElementConfigurations,
			path: cmModule.networkElementConfigurations,
		},
		{
			condition: true,
			Component: RUStatusDashboard,
			path: ruStatusDashboard,
		},
		{
			condition: !isManageNetworkElementConfigurationAccess,
			Component: ChangeMonitoring,
			path: cmModule.changeMonitoring,
		},
		{
			condition: isCmModuleAccess && isDataSourcesAccess,
			Component: DataSources,
			path: cmModule.dataSources,
		},
		{
			condition: isConfigurationDataModelManagement && (isMavenirVendor || isOranVendor),
			Component: ConfigurationDataModelManagement,
			path: cmModule.configurationDataModelManagement,
		},
		{
			condition: isConfigurationDataModelManagement && isOranVendor,
			Component: ConfigurationTemplates,
			path: cmModule.configurationTemplates,
		},
		{
			condition: isConfigurationDataModelManagement && isOranVendor,
			Component: OperationRules,
			path: cmModule.operationRules,
		},
		{
			condition: isSMOLightAccess,
			Component: ManageMiddlewareSMO,
			path: middlewareSMO,
		},
		{
			condition: isSMOLightAccess,
			Component: ManageNetworkElements,
			path: networkElements,
		},
		{
			condition: isSMOLightAccess,
			Component: VesEventsTable,
			path: vesEvents,
		},
		{
			condition: true,
			Component: ApiDocumentation,
			path: reports.index,
		},
		{
			condition: false,
			Component: null,
			path: '/',
		},
	];

	const { selectedVendor } = useAppSelector(configurationManagement);

	const firstNotHiddenItem = menuItems.find(item => !item.isHidden)?.route;

	const sideBarItems = toSideBarMenuItems({
		sideBarItems: menuItems,
		sideBarGroups: adminSideBarGroups,
		currentVendor: selectedVendor?.value,
		allVendors: user?.organization?.availableVendors?.map(v => v.name),
	});

	useEffect(() => {
		dispatch(setVendorDrivenFeatures(menuItems.filter(i => i?.vendors)));
	}, [dispatch, menuItems]);

	return isLoadingVendorsOptions || isLoadingCurrentUser || isLoadingAlarmVendors
		? DEFAULT_ADMIN_PANEL_ROUTES
		: {
			sideBarItems,
			routesList,
			firstNotHiddenItem,
			isLoading: isLoadingCurrentUser || isLoadingVendorsOptions || isLoadingAlarmVendors,
		};
};
